import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { apv as api } from '../services/api';
import FiltresPanel from './FiltresPanel';
import { SelecteurMaille, useRegroupement, construireArbre, axesDisponibles } from './regroupements';
import './Creances.css';
import './Stock.css';
import './OrEnCours.css';
import { usePanneau, basculer } from './ventesUtils';

// ─── APV — OR en cours (ordres de réparation non facturés) ──────────────────
// Photo du jour de l'export Datacar « OR En Cours », lue dans A97 DATA.
// Un OR « valorisé » porte au moins une ligne typée (pièce, MO, kit…) ; un OR
// « vide » n'en porte aucune : c'est une coquille ouverte à clôturer, comptée à
// part pour ne pas polluer l'encours. Le service est l'atelier du
// réceptionnaire, déduit de ses OR facturés (Mécanique, Rapide, Carrosserie,
// Magasin…) ; « Non rattaché » = réceptionnaire jamais vu sur une facture.
// Même vocabulaire visuel que Créances / Stock : la rampe d'ancienneté va du
// vert (frais) au rouge (> 1 an).

const COULEURS = ['#2E5BFF', '#C9A84C', '#0F766E', '#9D174D', '#4D7C0F', '#B45309', '#475569'];
// Palette d'ancienneté : elle ne sert plus QU'AU GRAPHIQUE d'évolution et à sa
// légende. Les barres de répartition et les pastilles de tableau qui
// l'utilisaient ont été retirées le 2026-09-18 — un tableau se lit en chiffres,
// un graphique a besoin de couleurs.
// Pastille de nature : la couleur vit dans OrEnCours.css (une teinte par thème ;
// en dur ici, « Client » bleu nuit disparaissait en mode sombre — 2026-10-24).
// Famille du motif, abrégée dans la pastille (libellé complet au survol).
const MOTIF_COURT = { 'Interne atelier, retours, retouches': 'Interne atelier', 'Garanties internes': 'Garantie interne', 'Cadeaux clients': 'Cadeau client',
  'Magasin et pièces': 'Magasin', 'LLD et location': 'LLD', 'Motif non codé': 'non codé', 'Code « ne pas utiliser »': 'code à proscrire' };
const motifCourt = (f) => MOTIF_COURT[f] || f.replace(/^Garanties? /, '');
const natureClasse = (n) => `oc-nat-pill n-${({ Client: 'client', Garantie: 'garantie', Cession: 'cession', Assurance: 'assurance' })[n] || 'autre'}`;
const nf = new Intl.NumberFormat('fr-FR');
const eur = (v) => nf.format(Math.round(v || 0)) + ' €';
const cM = (v) => {
  v = Math.round(v || 0);
  const a = Math.abs(v);
  if (a >= 1e6) return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(v / 1e6) + ' M€';
  if (a >= 1e3) return nf.format(Math.round(v / 1e3)) + ' k€';
  return v + ' €';
};
const kc = (v) => (!v ? '·' : Math.abs(v) < 500 ? '< 1 k' : nf.format(Math.round(v / 1000)) + ' k');
const pct = (a, b) => (b ? Math.round((a / b) * 100) : 0);
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '—');
// Dates du détail des OR en JJ/MM/AA : trois colonnes de dates, la place compte.
const dateCourte = (d) => (d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: '2-digit' }) : '—');
const anciens = (t) => t.slice(3).reduce((a, x) => ({ n: a.n + x.n, v: a.v + x.v }), { n: 0, v: 0 });

// « OR à traiter » (2026-09-28) : les OR valorisés regroupés selon un axe
// choisi dans l'en-tête (sélecteur st-seg), puis la liste des OR d'un groupe.
// « Collaborateur » = le réceptionnaire porté par l'OR ; « Atelier » = son
// service ; « Type » = la nature du document (client, garantie, cession…).
const VUES_OR = [
  { c: 'marque', l: 'Marque', cle: (o) => o.marque || '— NR' },
  { c: 'collaborateur', l: 'Collaborateur', cle: (o) => o.collaborateur || '— NR' },
  { c: 'nature', l: 'Type', cle: (o) => o.nature || 'Autre' },
  // Motif (2026-10-27) : type de cession / de garantie saisi dans Datacar,
  // regroupé en familles par A97 DATA (deux référentiels de codes : 60131…
  // partout, N1/A1/O1… chez Sorec). Un OR client n'a pas de motif.
  { c: 'motif', l: 'Motif', cle: (o) => (o.motif_famille ? `${o.nature} · ${o.motif_famille}` : (o.nature || 'Autre')) },
  { c: 'tranche', l: 'Ancienneté', cle: (o) => o.tranche },
  { c: 'service', l: 'Atelier', cle: (o) => o.service || 'Non rattaché' },
];
const MAX_LISTE_OR = 200;
// Filtres croisés (2026-10-24, même geste que « Stock par marque ») : chaque
// ligne cliquée devient un filtre ; plusieurs lignes d'un onglet se cumulent
// (« FORD ou PEUGEOT »), les onglets se croisent (une marque ET une
// ancienneté). Chaque onglet est calculé sur les filtres des AUTRES onglets,
// pour que ses propres lignes restent visibles et cliquables (CLAUDE.md §4 bis
// règle 4). Un seul état : l'URL, reflétée dans le panneau. « | » sépare les
// valeurs : un nom de collaborateur peut contenir une virgule.
const CLE_URL_OR = { marque: 'or_marques', collaborateur: 'or_collaborateurs', nature: 'or_types', motif: 'or_motifs', tranche: 'or_ages', service: 'or_ateliers' };
const SEP = '|';
const lireFiltresOr = (params) => Object.fromEntries(VUES_OR.map((v) => {
  const brut = (params.get(CLE_URL_OR[v.c]) || '').split(SEP).filter(Boolean);
  return [v.c, v.c === 'tranche' ? brut.map(Number).filter(Number.isInteger) : brut];
}));
const passeOr = (o, filtres, sauf) => VUES_OR.every((v) => v.c === sauf || !filtres[v.c].length || filtres[v.c].includes(v.cle(o)));
const NOMS_VUES = { marque: ['marque', 'marques'], collaborateur: ['collaborateur', 'collaborateurs'], nature: ['type', 'types'], motif: ['motif', 'motifs'], tranche: ['tranche', 'tranches'], service: ['atelier', 'ateliers'] };

export default function OrEnCours({ branche = 'auto-antilles' }) {
  const [params, setParams] = useSearchParams();
  // Activité (2026-10-22) : atelier par défaut (rien dans l'adresse), magasin ou tout.
  const activite = ['magasin', 'tout'].includes(params.get('activite')) ? params.get('activite') : '';
  const setActivite = (v) => { const next = new URLSearchParams(params); if (v) next.set('activite', v); else next.delete('activite'); setParams(next, { replace: true }); };
  // Travail pointé (2026-10-24) : tous, avec ou sans temps compagnon pointé sur
  // l'OR (pointages de la Productivité). Filtre serveur, porté par l'URL.
  const pointage = ['avec', 'sans'].includes(params.get('pointage')) ? params.get('pointage') : '';
  const setPointage = (v) => { const next = new URLSearchParams(params); if (v) next.set('pointage', v); else next.delete('pointage'); setParams(next, { replace: true }); };
  const [data, setData] = useState(null);
  const [erreur, setErreur] = useState('');
  const [unite, setUnite] = useState('n');   // 'n' OR | 'v' k€
  const [ouverts, setOuverts] = useState(() => new Set());
  const [vueOr, setVueOr] = useState('marque');
  // Bloc « Détail des OR » : on l'amène à l'écran quand une ligne est cliquée.
  const filtresOr = useMemo(() => lireFiltresOr(params), [params]);
  const setFiltresOr = (f) => {
    const next = new URLSearchParams(params);
    VUES_OR.forEach((v) => { const l = f[v.c]; if (l.length) next.set(CLE_URL_OR[v.c], l.join(SEP)); else next.delete(CLE_URL_OR[v.c]); });
    setParams(next, { replace: true });
  };
  const nbFiltresOr = VUES_OR.reduce((n, v) => n + filtresOr[v.c].length, 0);
  // Le panneau commun : replié d'office sur téléphone (ventesUtils.js).
  const [panneauOuvert, togglePanneau] = usePanneau();

  useEffect(() => {
    let actif = true;
    setData(null); setErreur('');
    api.orEnCours(branche, activite || undefined, pointage || undefined).then((r) => { if (actif) setData(r.data); }).catch((e) => { if (actif) setErreur(e.response?.data?.error || 'Erreur de chargement'); });
    return () => { actif = false; };
  }, [branche, activite, pointage]);

  const labels = useMemo(() => data?.labels || [], [data]);
  const societes = useMemo(() => data?.societes || [], [data]);
  const couleurDe = useMemo(() => { const m = {}; societes.forEach((s, i) => { m[s.code] = COULEURS[i % COULEURS.length]; }); return m; }, [societes]);

  const societesUrl = params.get('societes') || '';
  const selected = useMemo(() => {
    if (societesUrl === '__aucune__') return [];
    if (!societesUrl) return societes.map((s) => s.code);
    return societesUrl.split(',').filter(Boolean);
  }, [societesUrl, societes]);
  const selSet = new Set(selected);
  // Filtre « OR vides » du panneau (2026-10-22) : masqués de toute la page —
  // indicateurs, colonnes « Vides », groupes qui n'en contiennent que.
  const masquerVides = params.get('vides') === 'masquer';
  const setVides = (v) => { const next = new URLSearchParams(params); if (v) next.set('vides', v); else next.delete('vides'); setParams(next, { replace: true }); };
  const setFiltre = (v) => { const next = new URLSearchParams(params); if (v) next.set('societes', v); else next.delete('societes'); setParams(next, { replace: true }); };
  const toggleSociete = (code) => {
    const suivantes = selSet.has(code) ? selected.filter((c) => c !== code) : [...selected, code];
    setFiltre(suivantes.length === 0 ? '__aucune__' : suivantes.length === societes.length ? '' : suivantes.join(','));
  };
  const toggleOuvert = (k) => setOuverts((prev) => { const n = new Set(prev); if (n.has(k)) n.delete(k); else n.add(k); return n; });

  const sel = societes.filter((s) => selSet.has(s.code));
  const total = useMemo(() => {
    const t = { ors: 0, vides: 0, ht: 0, pieces: 0, mo: 0, jours: 0, pointes: 0, videsPointes: 0, heures: 0, heuresVides: 0, tranches: labels.map(() => ({ n: 0, v: 0 })), natures: {} };
    for (const s of sel) {
      t.ors += s.ors; t.vides += s.vides; t.ht += s.ht; t.pieces += s.pieces; t.mo += s.mo; t.jours += (s.ageMoyen || 0) * s.ors;
      t.pointes += s.pointes || 0; t.videsPointes += s.videsPointes || 0; t.heures += s.heures || 0; t.heuresVides += s.heuresVides || 0;
      s.tranches.forEach((x, i) => { t.tranches[i].n += x.n; t.tranches[i].v += x.v; });
      Object.entries(s.natures || {}).forEach(([n, x]) => { const c = t.natures[n] || (t.natures[n] = { ors: 0, ht: 0, anciens: 0 }); c.ors += x.ors; c.ht += x.ht; c.anciens += x.anciens; });
    }
    t.ageMoyen = t.ors ? Math.round(t.jours / t.ors) : null;
    return t;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societesUrl, societes, labels]);

  // Maille du bloc 1 (2026-09-28) : concession, concessions groupées, plaque.
  // Un regroupement ADDITIONNE ses concessions (mesures additives ; l'âge moyen
  // se repondère par le nombre d'OR) ; déplier un regroupement montre ses
  // concessions, déplier une concession ses services, comme avant.
  const maille = useRegroupement();
  const axes = useMemo(() => axesDisponibles(societes), [societes]);
  const sommerOR = (membres) => {
    const t = { ors: 0, vides: 0, ht: 0, pieces: 0, mo: 0, jours: 0, pointes: 0, videsPointes: 0, heures: 0, heuresVides: 0, tranches: labels.map(() => ({ n: 0, v: 0 })) };
    membres.forEach((m) => {
      t.ors += m.ors; t.vides += m.vides; t.ht += m.ht; t.pieces += m.pieces; t.mo += m.mo; t.jours += (m.ageMoyen || 0) * m.ors;
      t.pointes += m.pointes || 0; t.videsPointes += m.videsPointes || 0; t.heures += m.heures || 0; t.heuresVides += m.heuresVides || 0;
      m.tranches.forEach((x, i) => { t.tranches[i].n += x.n; t.tranches[i].v += x.v; });
    });
    return { ...t, ageMoyen: t.ors ? Math.round(t.jours / t.ors) : null };
  };
  const feuilles = useMemo(
    () => societes.filter((s) => selected.includes(s.code)).map((s) => ({ ...s, libelle: s.nom })),
    [societes, selected]);
  // Vue regroupée (concessions groupées, plaque) : aucun dépliage, le clic filtre.
  const regroupe = maille.niveau !== 'concession';
  const arbre = useMemo(
    // Regroupements fermés en permanence (2026-10-22) : en « Concessions
    // groupées », pas de dépliage vers le détail par concession.
    () => construireArbre(feuilles, maille.niveau, sommerOR, new Set(), (x) => x.ht),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [feuilles, maille.niveau, maille.ouverts, labels]);


  // Tous les OR de la sélection ; `listeOr` = les seuls valorisés (les vides
  // sont comptés à part, colonne « Vides »).
  const tousOr = useMemo(() => (data?.ors || []).filter((o) => selSet.has(o.societe)), [data, selected]); // eslint-disable-line react-hooks/exhaustive-deps
  const listeOr = useMemo(() => tousOr.filter((o) => !o.vide), [tousOr]);
  const groupesOr = useMemo(() => {
    const v = VUES_OR.find((x) => x.c === vueOr);
    const m = new Map();
    for (const o of tousOr) {
      if (!passeOr(o, filtresOr, vueOr)) continue;
      const k = v.cle(o);
      const g = m.get(k) || { cle: k, n: 0, vides: 0, ht: 0, pieces: 0, mo: 0, jours: 0, vieuxN: 0, vieuxV: 0, pointes: 0, heures: 0 };
      if (o.vide) { if (masquerVides) continue; g.vides += 1; m.set(k, g); continue; }
      if (o.heures) { g.pointes += 1; g.heures += o.heures; }
      g.n += 1; g.ht += o.ht || 0; g.pieces += o.pieces || 0; g.mo += o.mo || 0; g.jours += o.age || 0;
      if (o.age > 90) { g.vieuxN += 1; g.vieuxV += o.ht || 0; }
      m.set(k, g);
    }
    const out = [...m.values()].map((g) => ({ ...g, libelle: vueOr === 'tranche' ? labels[g.cle] : g.cle, ageMoyen: g.n ? Math.round(g.jours / g.n) : null }));
    return vueOr === 'tranche' ? out.sort((a, b) => a.cle - b.cle) : out.sort((a, b) => b.ht - a.ht);
  }, [tousOr, vueOr, labels, masquerVides, filtresOr]);
  // Détail : les OR qui passent TOUS les filtres. Valorisés d'abord (triés par
  // encours décroissant), puis les OR VIDES sur lesquels un compagnon a
  // pointé : du travail fait sans aucune ligne.
  const orDuGroupe = useMemo(() => {
    if (!nbFiltresOr) return [];
    const val = listeOr.filter((o) => passeOr(o, filtresOr, null));
    const videsPointes = masquerVides ? [] : tousOr.filter((o) => o.vide && o.heures && passeOr(o, filtresOr, null)).sort((a, b) => b.heures - a.heures);
    return [...val, ...videsPointes];
  }, [listeOr, tousOr, filtresOr, nbFiltresOr, masquerVides]);
  const basculerLigneOr = (cle) => setFiltresOr({
    ...filtresOr, [vueOr]: filtresOr[vueOr].includes(cle) ? filtresOr[vueOr].filter((x) => x !== cle) : [...filtresOr[vueOr], cle],
  });
  const retirerFiltreOr = (vue, cle) => setFiltresOr({ ...filtresOr, [vue]: filtresOr[vue].filter((x) => x !== cle) });
  const libelleFiltreOr = (vue, cle) => (vue === 'tranche' ? labels[cle] || '—' : cle);
  const pucesOr = VUES_OR.flatMap((v) => filtresOr[v.c].map((cle) => ({ vue: v.c, lib: v.l, cle, valeur: libelleFiltreOr(v.c, cle) })));
  // Le panneau ne liste que les filtres RETENUS ; décocher retire le filtre.
  const categoriesOr = pucesOr.map((p) => ({ code: `${p.vue}${SEP}${p.cle}`, nom: `${p.lib} : ${p.valeur}` }));
  const retirerCategorieOr = (code) => {
    const [vue, ...reste] = code.split(SEP); const brut = reste.join(SEP);
    retirerFiltreOr(vue, vue === 'tranche' ? Number(brut) : brut);
  };
  const toutRetirerOr = () => setFiltresOr(Object.fromEntries(VUES_OR.map((v) => [v.c, []])));
  const pt = data?.pointages?.dispo;   // pointages disponibles (Antilles)
  const depuisPt = data?.pointages?.depuis ? new Date(data.pointages.depuis).toLocaleDateString('fr-FR') : null;
  const h = (v) => (v ? `${nf.format(Math.round(v))} h` : '·');

  const Filtres = (
    <FiltresPanel
      ouvert={panneauOuvert} onToggle={togglePanneau}
      societesDispo={societes} societesActives={selected}
      onToggleSociete={toggleSociete}
      onToutesSocietes={() => setFiltre('')}
      onAucuneSociete={() => setFiltre('__aucune__')}
      couleurDe={couleurDe} nbActifs={(societesUrl ? 1 : 0) + (masquerVides ? 1 : 0) + (activite ? 1 : 0) + (pointage ? 1 : 0) + nbFiltresOr}
      onReset={() => { const next = new URLSearchParams(params); ['societes', 'vides', 'activite', 'pointage', ...Object.values(CLE_URL_OR)].forEach((k) => next.delete(k)); setParams(next, { replace: true }); }}
      categoriesDispo={categoriesOr} categoriesActives={categoriesOr.map((c) => c.code)}
      onToggleCategorie={retirerCategorieOr} titreCategories="OR à traiter" categoriesApres
      choix={[
        { titre: 'Activité', valeurs: [{ code: '', nom: 'Atelier' }, { code: 'magasin', nom: 'Magasin' }, { code: 'tout', nom: 'Tout (y compris OR sans service)' }], valeur: activite, onChoisir: setActivite },
        { titre: 'OR vides', valeurs: [{ code: '', nom: 'Afficher' }, { code: 'masquer', nom: 'Masquer' }], valeur: masquerVides ? 'masquer' : '', onChoisir: setVides },
        ...(pt ? [{ titre: 'Travail pointé', valeurs: [{ code: '', nom: 'Tous' }, { code: 'avec', nom: 'Avec travail pointé' }, { code: 'sans', nom: 'Sans travail pointé' }], valeur: pointage, onChoisir: setPointage }] : []),
      ]}
    />
  );

  if (erreur) return <main className="cr-page"><div className="bi-empty"><h2>Rapport indisponible</h2><p>{erreur}</p></div></main>;
  if (!data) return <main className="cr-page"><div className="cr-skel" /></main>;
  if (societes.length === 0) {
    return (<main className="cr-page"><div className="bi-empty"><h2>Aucun OR en cours dans votre périmètre</h2><p>Les ordres de réparation apparaîtront ici pour les sociétés que votre habilitation autorise.</p></div></main>);
  }

  const vide = selected.length === 0;
  const anc = anciens(total.tranches);
  const cell = (x) => (unite === 'n' ? (x.n || '·') : kc(x.v));
  const nomSel = selected.length === societes.length ? 'Toutes les sociétés' : selected.length === 1 ? sel[0]?.nom : `${selected.length} sociétés`;
  const garantie = total.natures.Garantie || { ors: 0, ht: 0, anciens: 0 };
  // Clés de tous les regroupements de la maille courante (arbre entièrement déplié).
  const clesGroupes = construireArbre(feuilles, maille.niveau, sommerOR, { has: () => true }, null)
    .filter((l) => !l.feuille).map((l) => l.cle);
  const tousOuverts = clesGroupes.every((k) => maille.ouverts.has(k))
    && sel.every((s) => ouverts.has(s.code) && s.services.every((srv) => ouverts.has(`${s.code}/${srv.nom}`)));
  const toutDeplier = () => {
    maille.ouvrirTout(clesGroupes);
    setOuverts(new Set(sel.flatMap((s) => [s.code, ...s.services.map((srv) => `${s.code}/${srv.nom}`)])));
  };
  const toutReplier = () => { maille.ouvrirTout([]); setOuverts(new Set()); };

  const K = [
    { l: 'OR en cours valorisés', val: nf.format(total.ors), meta: masquerVides ? nomSel : `${nomSel} · + ${nf.format(total.vides)} OR vides` },
    { l: 'Encours HT', val: cM(total.ht), meta: `pièces ${cM(total.pieces)} · MO ${cM(total.mo)}` },
    { l: 'Encours > 90 j', val: cM(anc.v), meta: `${pct(anc.v, total.ht)} % de l'encours · ${nf.format(anc.n)} OR`, alert: pct(anc.v, total.ht) >= 30 },
    ...(pt ? [{ l: 'OR avec travail pointé', val: nf.format(total.pointes), meta: `${pct(total.pointes, total.ors)} % des OR valorisés · ${h(total.heures)} pointées` }] : []),
    ...(masquerVides ? [] : [{ l: 'OR vides à clôturer', val: nf.format(total.vides), meta: pt && total.videsPointes ? `sans aucune ligne · dont ${nf.format(total.videsPointes)} pointés (${h(total.heuresVides)})` : 'ouverts sans aucune ligne', alert: total.vides > 0 }]),
    { l: 'Garantie en attente', val: cM(garantie.ht), meta: `${nf.format(garantie.ors)} OR · ${nf.format(garantie.anciens)} de plus de 90 j`, },
  ];

  // Une ligne du tableau hiérarchique (société / service / collaborateur).
  // `niveau` = profondeur (indentation) ; `genre` = type de ligne, qui porte le
  // style : 'grp' regroupement, 0 concession, 1 service, 2 collaborateur.
  // `onFiltre` (2026-10-22) : en vue regroupée, un clic sur une ligne filtre
  // toute la page sur ses sociétés (re-clic : toutes), comme les autres pages.
  const Ligne = ({ x, niveau, genre = niveau, cle, ouvrable, ouvert, nb, onToggle, onFiltre, actif }) => {
    const a = anciens(x.tranches);
    const p = pct(a.v, x.ht);
    return (
      <tr className={`oc-l${genre === 'grp' ? '0 oc-grp' : genre}${ouvrable || onFiltre ? ' tbl-clic' : ''}${actif ? ' actif' : ''}`}
        title={onFiltre ? (actif ? 'Revenir à toutes les sociétés' : 'Filtrer la page sur cette ligne') : undefined}
        onClick={onFiltre || (ouvrable ? () => (onToggle || toggleOuvert)(cle) : undefined)}>
        <td className="oc-nom">
          <span className="oc-indent" style={{ width: niveau * 18 }} />
          {ouvrable ? <span className={`oc-chev${ouvert ? ' on' : ''}`}>▸</span> : <span className="oc-chev vide" />}
          <span className="oc-lib">{x.nom}</span>
          {nb != null && <span className="oc-nb">{nb}</span>}
        </td>
        <td className="num strong">{nf.format(x.ors)}</td>
        {!masquerVides && <td className={`num${x.vides ? ' oc-vides' : ' muted'}`}>{x.vides ? nf.format(x.vides) : '·'}</td>}
        {pt && <td className={`num${x.pointes ? '' : ' muted'}`} title={`${h(x.heures)} pointées${x.videsPointes ? ` · + ${x.videsPointes} OR vides pointés` : ''}`}>{x.pointes ? nf.format(x.pointes) : '·'}</td>}
        <td className="num strong">{cM(x.ht)}</td>
        <td className="num">{cM(x.pieces)}</td>
        <td className="num">{cM(x.mo)}</td>
        <td className="num muted">{x.ageMoyen == null ? '—' : `${x.ageMoyen} j`}</td>
        {x.tranches.map((t, i) => <td key={i} className={`num${t.n ? '' : ' muted'}`}>{cell(t)}</td>)}
        <td className={`num${p >= 30 ? ' alert' : ''}`}>{x.ht ? `${p} %` : '·'}</td>
      </tr>
    );
  };

  return (
    <>
      <main className="cr-page">
        <div className="cr-head">
          <div>
            <h1>APV — OR en cours</h1>
          </div>
          <div className="cr-asof">Arrêté au <b>{dateFr(data.arrete)}</b><br /><span>{activite === 'magasin' ? 'Magasin' : activite === 'tout' ? 'Tous services' : 'Atelier'} · export « OR En Cours » Datacar</span></div>
        </div>

        {vide ? (
          <div className="bi-empty" style={{ marginTop: 20 }}><h2>Aucune société sélectionnée</h2><p>Cochez au moins une société dans les filtres à droite.</p></div>
        ) : (
          <>
            <div className="cr-kpis">
              {K.map((k, i) => (
                <div className={`cr-kpi${k.alert ? ' alert' : ''}`} key={i}>
                  <div className="cr-kpi-l">{k.l}</div>
                  <div className="cr-kpi-v">{k.val}</div>
                  <div className="cr-kpi-m">{k.meta}</div>
                </div>
              ))}
            </div>

            {/* ── Bloc 1 : concession ▸ service ▸ collaborateur ── */}
            <div className="cr-card">
              <div className="cr-card-h">
                <h2>Encours par concession, service et collaborateur</h2>
                <div className="st-tools">
                  {pt && depuisPt && <span className="cr-hint" title="Temps pointé par les compagnons sur l'OR (hors présence et travaux internes), source APV — Productivité">pointages depuis le {depuisPt}</span>}
                  <SelecteurMaille niveau={maille.niveau} setNiveau={maille.setNiveau} dispo={axes} />
                  {!regroupe && (
                    <button type="button" className="cr-btn ghost oc-btn" onClick={tousOuverts ? toutReplier : toutDeplier}>{tousOuverts ? 'Tout replier' : 'Tout déplier'}</button>
                  )}
                  <span className="cr-hint">ancienneté en</span>
                  <div className="st-seg">
                    <button type="button" className={unite === 'n' ? 'on' : ''} onClick={() => setUnite('n')}>OR</button>
                    <button type="button" className={unite === 'v' ? 'on' : ''} onClick={() => setUnite('v')}>k€</button>
                  </div>
                </div>
              </div>
              <div className="cr-scroll">
                <table className="cr-tbl cr-tbl-soc st-tbl oc-tbl tbl-dense">
                  <thead><tr>
                    <th>Concession / service / collaborateur</th>
                    <th className="num">OR</th>{!masquerVides && <th className="num">Vides</th>}
                    {pt && <th className="num" title="OR valorisés sur lesquels un compagnon a pointé du temps (survol : heures)">Pointés</th>}
                    <th className="num">Encours HT</th><th className="num">Pièces</th><th className="num">MO</th><th className="num">Âge moyen</th>
                    {labels.map((l, i) => <th key={i} className="num">{l}</th>)}
                    <th className="num">&gt; 90 j</th>
                  </tr></thead>
                  <tbody>
                    {arbre.map((r) => {
                      if (!r.feuille) {
                        const cible = (r.membres || []).join(',');
                        return <Ligne key={r.cle} x={{ ...r, nom: r.libelle }} niveau={r.niveau} genre="grp" cle={r.cle}
                          ouvrable={false} nb={`${r.nbEnfants} concessions`}
                          onFiltre={() => setFiltre(basculer(societesUrl, cible))} actif={societesUrl === cible} />;
                      }
                      const s = r; const d = r.niveau;
                      return (
                        <React.Fragment key={r.cle}>
                          {regroupe
                            // Vue regroupée : une concession seule dans son groupe (Blue) ne se
                            // déplie pas non plus ; le clic filtre la page.
                            ? <Ligne x={s} niveau={d} genre={0} cle={s.code} ouvrable={false} nb={`${s.services.length} services`}
                                onFiltre={() => setFiltre(basculer(societesUrl, s.code))} actif={societesUrl === s.code} />
                            : <Ligne x={s} niveau={d} genre={0} cle={s.code} ouvrable={s.services.length > 0} ouvert={ouverts.has(s.code)} nb={`${s.services.length} services`} />}
                          {!regroupe && ouverts.has(s.code) && s.services.map((srv) => {
                            const k = `${s.code}/${srv.nom}`;
                            return (
                              <React.Fragment key={k}>
                                <Ligne x={srv} niveau={d + 1} genre={1} cle={k} ouvrable={srv.collaborateurs.length > 0} ouvert={ouverts.has(k)} nb={`${srv.collaborateurs.length} collab.`} />
                                {ouverts.has(k) && srv.collaborateurs.map((c) => <Ligne key={`${k}/${c.nom}`} x={c} niveau={d + 2} genre={2} cle={`${k}/${c.nom}`} ouvrable={false} />)}
                              </React.Fragment>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                  {sel.length > 1 && (
                    <tfoot><tr>
                      <td>Total</td>
                      <td className="num strong">{nf.format(total.ors)}</td>
                      {!masquerVides && <td className="num oc-vides">{nf.format(total.vides)}</td>}
                      {pt && <td className="num" title={`${h(total.heures)} pointées`}>{nf.format(total.pointes)}</td>}
                      <td className="num strong">{cM(total.ht)}</td>
                      <td className="num">{cM(total.pieces)}</td>
                      <td className="num">{cM(total.mo)}</td>
                      <td className="num muted">{total.ageMoyen == null ? '—' : `${total.ageMoyen} j`}</td>
                      {total.tranches.map((x, i) => <td key={i} className="num">{cell(x)}</td>)}
                      <td className={`num${pct(anc.v, total.ht) >= 30 ? ' alert' : ''}`}>{pct(anc.v, total.ht)} %</td>
                    </tr></tfoot>
                  )}
                </table>
              </div>
            </div>

            {/* ── Bloc 3 : OR à traiter, par axe (sélecteur st-seg) ── */}
            <div className="cr-card">
              <div className="cr-card-h">
                <h2>OR à traiter</h2>
                <div className="st-tools">
                  <div className="st-seg">
                    {VUES_OR.map((v) => (
                      <button key={v.c} type="button" className={vueOr === v.c ? 'on' : ''}
                        onClick={() => setVueOr(v.c)}>{v.l}{filtresOr[v.c].length > 0 && <span className="st-seg-nb">{filtresOr[v.c].length}</span>}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="st-sous-h">
                <span className="cr-hint">{nf.format(groupesOr.length)} {NOMS_VUES[vueOr][groupesOr.length > 1 ? 1 : 0]} · {nf.format(groupesOr.reduce((n, g) => n + g.n, 0))} OR valorisés · cliquer une ou plusieurs lignes pour filtrer le détail</span>
              </div>
              <div className="cr-scroll">
                {groupesOr.length === 0 ? <p className="cr-empty-tbl">Aucun OR valorisé dans la sélection.</p> : (
                  <table className="cr-tbl cr-tbl-detail oc-grp-or tbl-dense">
                    <thead><tr>
                      <th>{VUES_OR.find((v) => v.c === vueOr).l}</th>
                      <th className="num">OR</th>{!masquerVides && <th className="num">Vides</th>}
                      {pt && <th className="num">Pointés</th>}{pt && <th className="num">H pointées</th>}
                      <th className="num">Encours HT</th><th className="num">Pièces</th><th className="num">MO</th>
                      <th className="num">Âge moyen</th><th className="num">&gt; 90 j</th>
                    </tr></thead>
                    <tbody>
                      {groupesOr.map((g) => {
                        const actif = filtresOr[vueOr].includes(g.cle);
                        return (
                          <tr key={String(g.cle)} className={`tbl-clic${actif ? ' actif' : ''}`} title={actif ? 'Retirer ce filtre' : 'Filtrer le détail sur cette ligne'}
                            onClick={() => basculerLigneOr(g.cle)}>
                            <td>{vueOr === 'nature'
                              ? <span className={natureClasse(g.cle)}>{g.libelle}</span>
                              : g.libelle}</td>
                            <td className="num strong">{g.n ? nf.format(g.n) : '·'}</td>
                            {!masquerVides && <td className={`num${g.vides ? ' oc-vides' : ' muted'}`}>{g.vides ? nf.format(g.vides) : '·'}</td>}
                            {pt && <td className={`num${g.pointes ? '' : ' muted'}`}>{g.pointes ? nf.format(g.pointes) : '·'}</td>}
                            {pt && <td className={`num${g.heures ? '' : ' muted'}`}>{h(g.heures)}</td>}
                            <td className="num strong">{cM(g.ht)}</td>
                            <td className="num">{cM(g.pieces)}</td>
                            <td className="num">{cM(g.mo)}</td>
                            <td className="num muted">{g.ageMoyen == null ? '—' : `${nf.format(g.ageMoyen)} j`}</td>
                            <td className={`num${pct(g.vieuxV, g.ht) >= 30 ? ' alert' : ''}`}>{g.vieuxN ? `${nf.format(g.vieuxN)} · ${pct(g.vieuxV, g.ht)} %` : '·'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

            </div>

            {/* ── Bloc 4 (2026-10-22) : le détail des OR de la ligne cliquée
                dans « OR à traiter », dans un bloc à part en dessous. ── */}
              {nbFiltresOr > 0 && (
                <div className="cr-card">
                  <div className="cr-card-h">
                    <h2>Détail des OR</h2>
                    <div className="st-tools">
                      <span className="cr-hint">
                        {pt && depuisPt ? `pointages depuis le ${depuisPt} · ` : ''}{nf.format(orDuGroupe.length)} OR
                        {orDuGroupe.length > MAX_LISTE_OR ? ` · ${MAX_LISTE_OR} premiers affichés` : ''}
                      </span>
                      <button type="button" className="cr-btn ghost" onClick={toutRetirerOr}>Fermer</button>
                    </div>
                  </div>
                  <div className="st-sous-h">
                    {pucesOr.map((p) => (
                      <span className="st-puce" key={`${p.vue}|${p.cle}`}>
                        {p.lib} : <b>{p.valeur}</b>
                        <button type="button" onClick={() => retirerFiltreOr(p.vue, p.cle)} aria-label="Retirer ce filtre" title="Retirer ce filtre">×</button>
                      </span>
                    ))}
                    {nbFiltresOr > 1 && <button type="button" className="st-lien" onClick={toutRetirerOr}>tout retirer</button>}
                  </div>
                  <div className="cr-scroll oc-liste">
                    <table className="cr-tbl cr-tbl-detail oc-anc tbl-dense">
                      <thead><tr>
                        <th>Société</th><th>N° OR</th><th>Atelier</th><th>Collaborateur</th><th>Type</th><th>Marque</th>
                        <th className="num" title="En rouge : ouvert depuis plus d'un an">Créé le</th><th className="num">Dernier mvt</th>
                        <th className="num">Pièces</th><th className="num">MO</th><th className="num">Encours HT</th>
                        {pt && <><th className="num" title="Temps pointé par les compagnons (survol : nombre de compagnons)">H pointées</th><th className="num">Dern. pointage</th></>}
                      </tr></thead>
                      <tbody>
                        {orDuGroupe.slice(0, MAX_LISTE_OR).map((a) => (
                          <tr key={`${a.societe}-${a.num_or}`}>
                            <td>{a.societeNom}</td>
                            <td className="oc-or">{a.num_or}</td>
                            <td>{a.service}</td>
                            <td>{a.collaborateur}</td>
                            <td>
                              <span className={natureClasse(a.nature)}
                                title={a.motif_famille ? `${a.nature} · ${a.motif_famille} — ${a.motif || 'motif non codé dans Datacar'}${a.destinataire ? ` · destinataire : ${a.destinataire}` : ''}` : undefined}>
                                {a.nature}{a.motif_famille ? ` · ${motifCourt(a.motif_famille)}` : ''}
                              </span>
                            </td>
                            <td className="cd">{a.marque || '—'}</td>
                            <td className={`num cd${a.age > 365 ? ' alert' : ''}`} title={`${nf.format(a.age)} jours`}>{dateCourte(a.creation)}</td>
                            <td className="num cd">{dateCourte(a.maj)}</td>
                            <td className="num">{a.vide ? '·' : eur(a.pieces)}</td>
                            <td className="num">{a.vide ? '·' : eur(a.mo)}</td>
                            <td className={`num strong${a.vide ? ' oc-vides' : ''}`}>{a.vide ? 'OR vide' : eur(a.ht)}</td>
                            {pt && <>
                              <td className={`num${a.heures ? ' strong' : ' muted'}`} title={a.compagnons ? `${a.compagnons} compagnon${a.compagnons > 1 ? 's' : ''}` : undefined}>{a.heures ? `${nf.format(Math.round(a.heures * 10) / 10)} h` : '·'}</td>
                              <td className="num cd">{a.dernier_pointage ? dateCourte(a.dernier_pointage) : '·'}</td>
                            </>}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
          </>
        )}
      </main>
      {Filtres}
    </>
  );
}
